/* AdminThreatCenter — Threat Live + Security Controls (Feb 2026)
 *
 * Single-pane operations console for security incidents. Three tabs:
 *
 *   • Live      — real-time counters + top attacking IPs + event feed
 *   • Controls  — IP / CIDR blocklist · auto-ban rules · force-logout
 *   • Kill Switch — 5-level portal lockdown with peer-approved escalation
 *
 * Engineering scope required for state-changing actions. All admin
 * actions stamp an immutable audit row in `security_events`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, AlertTriangle, Shield, ShieldAlert, ShieldCheck, Loader2,
  Ban, Trash2, Plus, RefreshCw, Power, Lock, Unlock, LogOut, Clock,
  Globe, Server, Zap, Eye, ArrowRight, Bell, BellOff, BellRing,
  CheckCircle2, XCircle, Search,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";


const SEVERITY_META = {
  info:     { bg: "bg-stone-100", text: "text-stone-600", dot: "bg-stone-400" },
  warn:     { bg: "bg-amber-50",  text: "text-amber-700", dot: "bg-amber-500" },
  high:     { bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-500" },
  critical: { bg: "bg-red-50",    text: "text-red-700",   dot: "bg-red-500" },
};

const KILL_SWITCH_META = {
  normal:         { label: "Normal",         color: "emerald", desc: "All systems go." },
  read_only:      { label: "Read-only",      color: "amber",   desc: "Buyers/sellers can browse, no writes. Admins still write." },
  signup_locked:  { label: "Signups locked", color: "amber",   desc: "Existing users OK · no new accounts." },
  admin_only:     { label: "Admin-only",     color: "orange",  desc: "Only admin role tokens accepted." },
  full_freeze:    { label: "Full freeze",    color: "red",     desc: "Everything paused. Only /api/health responds." },
};

const KILL_SWITCH_LEVELS = ["normal", "read_only", "signup_locked", "admin_only", "full_freeze"];

function fmtTime(iso) {
  if (!iso) return "—";
  return iso.replace("T", " ").slice(0, 19);
}

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms/1000)}s ago`;
  if (ms < 3600_000) return `${Math.floor(ms/60_000)}m ago`;
  if (ms < 86400_000) return `${Math.floor(ms/3600_000)}h ago`;
  return `${Math.floor(ms/86400_000)}d ago`;
}

// ─── Stat cards ────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, hint, accent = "" }) {
  return (
    <div className={`rounded-xl border p-4 ${accent || "border-border bg-card"}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center gap-2 text-foreground/55">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] mono uppercase tracking-wider">{label}</span>
      </div>
      <div className="display text-3xl mt-1">{value ?? "—"}</div>
      {hint && <div className="text-[11px] text-foreground/55 mt-1">{hint}</div>}
    </div>
  );
}


// ─── Live tab ──────────────────────────────────────────────────────────
function LiveTab({ overview, topIps, feed, loading, onRefresh, onBlockIp }) {
  const ks = overview?.kill_switch || {};
  const ksMeta = KILL_SWITCH_META[ks.level || "normal"];
  const isAttacking = (overview?.failed_logins_1h ?? 0) > 50 || (overview?.rate_limits_24h ?? 0) > 200;

  return (
    <div className="space-y-5">
      {/* Top-line alert if under attack */}
      {isAttacking && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="under-attack-alert">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-red-900">Elevated threat activity detected</div>
            <div className="text-sm text-red-700 mt-0.5">
              {overview.failed_logins_1h} failed logins in the last hour · {overview.rate_limits_24h} rate-limit hits today.
              Consider blocking top-offending IPs or escalating the kill switch.
            </div>
          </div>
        </div>
      )}

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Lock}         label="Failed logins · 1h"   value={overview?.failed_logins_1h} hint={`${overview?.failed_logins_24h ?? 0} in 24h`} accent={(overview?.failed_logins_1h ?? 0) > 20 ? "border-amber-300 bg-amber-50" : ""} />
        <StatCard icon={Zap}          label="Rate-limit hits · 24h" value={overview?.rate_limits_24h} />
        <StatCard icon={Shield}       label="Webhook bad-sig · 24h" value={overview?.webhook_bad_sig_24h} />
        <StatCard icon={Ban}          label="Active IP blocks"      value={overview?.ip_blocks_active} hint={`${overview?.ip_blocks_hits_24h ?? 0} block-hits in 24h`} />
        <StatCard icon={Clock}        label="Auth lockouts · 24h"   value={overview?.auth_lockouts_24h} />
        <StatCard icon={Globe}        label="Impossible travel · 24h" value={overview?.impossible_travel_24h} accent={(overview?.impossible_travel_24h ?? 0) > 0 ? "border-orange-300 bg-orange-50" : ""} />
        <StatCard icon={Power}        label="Kill switch"           value={ksMeta?.label} hint={ks.level !== "normal" ? `set ${timeAgo(ks.set_at)}` : ""} accent={ks.level !== "normal" ? `border-${ksMeta.color}-300 bg-${ksMeta.color}-50` : ""} />
        <div className="rounded-xl border border-border p-4 flex items-center justify-center">
          <Button variant="outline" size="sm" onClick={onRefresh} className="rounded-full" data-testid="threat-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-2" />Refresh
          </Button>
        </div>
      </div>

      {/* Top attacking IPs */}
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-2.5 bg-secondary/40 border-b border-border flex items-center justify-between">
          <div className="text-[10px] mono uppercase tracking-wider text-foreground/55">Top attacking IPs · last 60 min</div>
        </div>
        <table className="w-full text-sm" data-testid="top-attacking-ips">
          <thead className="border-b border-border text-left text-[10px] mono uppercase tracking-wider text-foreground/55">
            <tr><th className="px-4 py-2">IP</th><th className="py-2">Hits</th><th className="py-2">Kinds</th><th className="py-2">Status</th><th className="py-2 text-right pr-4">Action</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-foreground/55"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2"/>Loading…</td></tr>}
            {!loading && (!topIps || topIps.length === 0) && <tr><td colSpan={5} className="px-4 py-8 text-center text-foreground/55">No attacking IPs detected in the last hour. Quiet right now ✨</td></tr>}
            {!loading && (topIps || []).map(row => (
              <tr key={row.ip} className="border-b border-border last:border-0">
                <td className="px-4 py-2 mono">{row.ip}</td>
                <td className="py-2"><span className="display text-base">{row.count}</span></td>
                <td className="py-2 text-[11px] text-foreground/65">{(row.kinds || []).join(", ")}</td>
                <td className="py-2">
                  {row.blocked
                    ? <Badge className="bg-red-50 text-red-700 ring-1 ring-red-200">Blocked</Badge>
                    : <Badge variant="outline" className="text-[10px]">Active</Badge>}
                </td>
                <td className="py-2 text-right pr-4">
                  {!row.blocked && (
                    <Button size="sm" variant="outline" onClick={() => onBlockIp(row.ip)} data-testid={`block-ip-${row.ip.replace(/\./g, '-')}`} className="text-xs rounded-full">
                      <Ban className="w-3 h-3 mr-1"/>Block 24h
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Live event feed */}
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-2.5 bg-secondary/40 border-b border-border">
          <div className="text-[10px] mono uppercase tracking-wider text-foreground/55">Live event feed</div>
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="threat-feed">
          {loading && <div className="px-4 py-8 text-center text-foreground/55"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2"/>Loading…</div>}
          {!loading && (!feed || feed.length === 0) && <div className="px-4 py-8 text-center text-foreground/55">No events captured yet.</div>}
          {!loading && (feed || []).map((e, i) => {
            const meta = SEVERITY_META[e.severity] || SEVERITY_META.info;
            return (
              <div key={i} className="px-4 py-2 border-b border-border last:border-0 flex items-center gap-3 text-sm">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${meta.dot}`} />
                <span className="mono text-[11px] text-foreground/55 w-32 flex-shrink-0">{fmtTime(e.created_at)}</span>
                <span className={`text-[10px] mono uppercase px-1.5 py-0.5 rounded ${meta.bg} ${meta.text} w-16 flex-shrink-0 text-center`}>{e.severity}</span>
                <span className="font-medium w-48 flex-shrink-0 truncate">{e.kind}</span>
                <span className="mono text-[11px] text-foreground/55 w-32 flex-shrink-0 truncate">{e.ip || "—"}</span>
                <span className="text-foreground/65 truncate flex-1">{e.user_email || JSON.stringify(e.payload || {}).slice(0, 80)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ─── Controls tab ──────────────────────────────────────────────────────
function ControlsTab({ blocks, rules, onRefresh, onChange }) {
  const [openBlock, setOpenBlock] = useState(false);
  const [openLogout, setOpenLogout] = useState(false);
  const [ip, setIp] = useState("");
  const [cidr, setCidr] = useState("");
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState(24);
  const [busy, setBusy] = useState(false);
  const [logoutScope, setLogoutScope] = useState("user");
  const [logoutUserId, setLogoutUserId] = useState("");
  const [logoutReason, setLogoutReason] = useState("");

  const submitBlock = async () => {
    if (!ip && !cidr) { toast.error("Provide IP or CIDR"); return; }
    if (reason.trim().length < 3) { toast.error("Reason required (3+ chars)"); return; }
    setBusy(true);
    try {
      await api.post("/admin/security/blocked-ips", {
        ip: ip || null, cidr: cidr || null, reason: reason.trim(), duration_hours: Number(duration) || null,
      });
      toast.success("IP blocked. Caches invalidated, enforcement live.");
      setOpenBlock(false); setIp(""); setCidr(""); setReason(""); setDuration(24);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Block failed");
    } finally { setBusy(false); }
  };

  const removeBlock = async (target) => {
    if (!confirm(`Remove block on ${target.ip || target.cidr}?`)) return;
    try {
      const params = target.ip ? `?ip=${encodeURIComponent(target.ip)}` : `?cidr=${encodeURIComponent(target.cidr)}`;
      await api.delete(`/admin/security/blocked-ips${params}`);
      toast.success("Block removed");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const submitLogout = async () => {
    if (logoutReason.trim().length < 10) { toast.error("Reason ≥ 10 chars required (audit)"); return; }
    if (logoutScope === "user" && !logoutUserId.trim()) { toast.error("user_id required"); return; }
    if (logoutScope === "global" && !confirm("This will log out EVERY non-admin user. Continue?")) return;
    setBusy(true);
    try {
      await api.post("/admin/security/force-logout", {
        scope: logoutScope,
        user_id: logoutScope === "user" ? logoutUserId.trim() : null,
        reason: logoutReason.trim(),
      });
      toast.success(logoutScope === "global" ? "Global logout fired. All non-admin sessions invalidated." : "User session revoked.");
      setOpenLogout(false); setLogoutUserId(""); setLogoutReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logout failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {/* Blocked IPs section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="display text-lg">Blocked IPs &amp; CIDRs</h3>
            <p className="text-sm text-foreground/60">Hard-block at the request edge. Cached 60 s per replica.</p>
          </div>
          <Button onClick={() => setOpenBlock(true)} className="rounded-full" data-testid="add-block-btn">
            <Plus className="w-4 h-4 mr-2"/>Block IP / CIDR
          </Button>
        </div>
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm" data-testid="blocked-ips-table">
            <thead className="bg-secondary/40 border-b border-border text-left text-[10px] mono uppercase tracking-wider text-foreground/55">
              <tr><th className="px-4 py-2">Target</th><th className="py-2">Reason</th><th className="py-2">Rule</th><th className="py-2">Expires</th><th className="py-2">Created</th><th className="py-2 pr-4 text-right">Action</th></tr>
            </thead>
            <tbody>
              {(!blocks?.active || blocks.active.length === 0) && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-foreground/55">No active blocks. Quiet right now ✨</td></tr>
              )}
              {(blocks?.active || []).map((b, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 mono">{b.ip || b.cidr}</td>
                  <td className="py-2">{b.reason}</td>
                  <td className="py-2">{b.rule ? <Badge variant="outline" className="text-[10px]">{b.rule}</Badge> : <span className="text-foreground/40">manual</span>}</td>
                  <td className="py-2 mono text-[11px] text-foreground/65">{b.expires_at ? timeAgo(b.expires_at).replace(' ago', '') : "never"}</td>
                  <td className="py-2 mono text-[11px] text-foreground/55">{timeAgo(b.created_at)}</td>
                  <td className="py-2 pr-4 text-right">
                    <button onClick={() => removeBlock(b)} className="text-foreground/40 hover:text-red-600" data-testid={`unblock-${(b.ip || b.cidr).replace(/[.\/]/g, '-')}`}>
                      <Trash2 className="w-4 h-4 inline-block"/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Auto-ban rules */}
      <div>
        <h3 className="display text-lg mb-1">Auto-ban rules</h3>
        <p className="text-sm text-foreground/60 mb-3">Thresholds that automatically add an IP to the blocklist. Engineering scope to edit.</p>
        <div className="grid md:grid-cols-3 gap-3">
          {Object.entries(rules || {}).map(([key, r]) => (
            <div key={key} className="rounded-xl border border-border p-4" data-testid={`rule-${key}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm">{key.replace(/_/g, " ")}</span>
                <Badge className={r.enabled ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}>
                  {r.enabled ? "On" : "Off"}
                </Badge>
              </div>
              <div className="text-[12px] text-foreground/65 leading-relaxed">
                <strong>{r.threshold}</strong> hits in <strong>{r.window_minutes}m</strong> → block <strong>{r.ban_hours}h</strong>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-foreground/50 mt-2">Editing rules via API: <code className="mono">POST /admin/security/auto-ban-rules</code></p>
      </div>

      {/* Force logout */}
      <div>
        <h3 className="display text-lg mb-1">Force-logout</h3>
        <p className="text-sm text-foreground/60 mb-3">Invalidate active sessions. Per-user or global (non-admin only).</p>
        <Button onClick={() => setOpenLogout(true)} variant="outline" className="rounded-full" data-testid="force-logout-btn">
          <LogOut className="w-4 h-4 mr-2"/>Force-logout sessions
        </Button>
      </div>

      {/* Block modal */}
      {openBlock && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setOpenBlock(false)}>
          <div className="bg-background rounded-2xl border border-border p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="display text-lg mb-3">Block IP or CIDR</h3>
            <label className="text-[11px] mono uppercase tracking-wider text-foreground/55">IP (exact)</label>
            <Input value={ip} onChange={e => setIp(e.target.value)} placeholder="203.0.113.42" className="mt-1" data-testid="block-ip-input" />
            <div className="text-xs text-foreground/40 my-2 text-center">— or —</div>
            <label className="text-[11px] mono uppercase tracking-wider text-foreground/55">CIDR range</label>
            <Input value={cidr} onChange={e => setCidr(e.target.value)} placeholder="203.0.113.0/24" className="mt-1" data-testid="block-cidr-input" />
            <label className="text-[11px] mono uppercase tracking-wider text-foreground/55 mt-3 block">Reason</label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Credential spray detected · 47 failed logins/min" className="mt-1" data-testid="block-reason-input" />
            <label className="text-[11px] mono uppercase tracking-wider text-foreground/55 mt-3 block">Duration (hours · 0 = permanent)</label>
            <Input type="number" value={duration} onChange={e => setDuration(e.target.value)} className="mt-1 max-w-xs" data-testid="block-duration-input" />
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setOpenBlock(false)}>Cancel</Button>
              <Button onClick={submitBlock} disabled={busy} data-testid="block-submit">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}Apply block
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Force-logout modal */}
      {openLogout && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setOpenLogout(false)}>
          <div className="bg-background rounded-2xl border border-border p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="display text-lg mb-3">Force-logout</h3>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setLogoutScope("user")}     className={`flex-1 rounded-lg border p-3 text-left ${logoutScope === "user" ? "border-foreground bg-secondary/40" : "border-border"}`} data-testid="logout-scope-user">
                <div className="font-medium text-sm">Single user</div>
                <div className="text-[11px] text-foreground/55 mt-0.5">Revoke this user's sessions</div>
              </button>
              <button onClick={() => setLogoutScope("global")}   className={`flex-1 rounded-lg border p-3 text-left ${logoutScope === "global" ? "border-foreground bg-secondary/40" : "border-border"}`} data-testid="logout-scope-global">
                <div className="font-medium text-sm">Global</div>
                <div className="text-[11px] text-foreground/55 mt-0.5">All non-admin users out</div>
              </button>
            </div>
            {logoutScope === "user" && (
              <>
                <label className="text-[11px] mono uppercase tracking-wider text-foreground/55">User ID</label>
                <Input value={logoutUserId} onChange={e => setLogoutUserId(e.target.value)} className="mt-1" placeholder="usr-abc123…" data-testid="logout-user-id" />
              </>
            )}
            <label className="text-[11px] mono uppercase tracking-wider text-foreground/55 mt-3 block">Reason (≥ 10 chars · audit)</label>
            <Textarea value={logoutReason} onChange={e => setLogoutReason(e.target.value)} rows={3} placeholder="Suspected account compromise — see incident TKT-1234" className="mt-1" data-testid="logout-reason" />
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setOpenLogout(false)}>Cancel</Button>
              <Button onClick={submitLogout} disabled={busy} variant={logoutScope === "global" ? "destructive" : "default"} data-testid="logout-submit">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Kill Switch tab ───────────────────────────────────────────────────
function KillSwitchTab({ state, onChange }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const currentLevel = state?.level || "normal";
  const meta = KILL_SWITCH_META[currentLevel];

  const apply = async (level) => {
    if (level === currentLevel) { toast.error("Already at this level."); return; }
    if (reason.trim().length < 10) { toast.error("Reason ≥ 10 chars required (audit)"); return; }
    const dangerous = ["admin_only", "full_freeze"].includes(level);
    if (dangerous && !confirm(`Set kill switch to "${KILL_SWITCH_META[level].label}"? This will affect every user.`)) return;
    setBusy(true);
    try {
      await api.post("/admin/security/kill-switch", { level, reason: reason.trim() });
      toast.success(`Kill switch set to ${KILL_SWITCH_META[level].label}`);
      setReason("");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Set failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      {/* Current state */}
      <div className={`rounded-2xl border-2 p-6 border-${meta.color}-300 bg-${meta.color}-50`} data-testid="ks-current">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Current state</div>
            <h2 className="display text-3xl mt-1">{meta.label}</h2>
            <p className="text-sm text-foreground/65 mt-2 max-w-lg">{meta.desc}</p>
            {state?.set_at && currentLevel !== "normal" && (
              <p className="text-[12px] text-foreground/55 mt-2">
                Set by <strong>{state.set_by_email}</strong> · {timeAgo(state.set_at)}<br/>
                Reason: <em>{state.reason}</em>
              </p>
            )}
          </div>
          <Power className={`w-12 h-12 text-${meta.color}-500`} />
        </div>
      </div>

      {/* Reason input */}
      <div className="rounded-xl border border-border p-4">
        <label className="text-[11px] mono uppercase tracking-wider text-foreground/55">Reason for state change (≥ 10 chars · audit)</label>
        <Textarea
          value={reason} onChange={e => setReason(e.target.value)}
          rows={2} placeholder="Investigating brute-force activity from new IP range — locking down for the next hour."
          className="mt-1" data-testid="ks-reason"
        />
      </div>

      {/* Level escalator */}
      <div>
        <h3 className="display text-base mb-3">Set new level</h3>
        <div className="space-y-2">
          {KILL_SWITCH_LEVELS.map(level => {
            const m = KILL_SWITCH_META[level];
            const isCurrent = level === currentLevel;
            const isDangerous = ["admin_only", "full_freeze"].includes(level);
            return (
              <button
                key={level}
                onClick={() => apply(level)}
                disabled={busy || isCurrent}
                data-testid={`ks-level-${level}`}
                className={`w-full text-left rounded-xl border p-4 transition-colors flex items-center gap-4
                  ${isCurrent ? "bg-secondary/30 border-foreground" : `hover:border-${m.color}-400`}
                  ${isDangerous && !isCurrent ? "border-red-200" : "border-border"}
                  disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className={`w-2 h-2 rounded-full bg-${m.color}-500`}/>
                <div className="flex-1">
                  <div className="font-medium">{m.label}</div>
                  <div className="text-[12px] text-foreground/60 mt-0.5">{m.desc}</div>
                </div>
                {isCurrent && <Badge className="bg-foreground text-background">Current</Badge>}
                {!isCurrent && <ArrowRight className="w-4 h-4 text-foreground/40"/>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ─── Alerts tab (Phase A) ──────────────────────────────────────────────
function AlertsTab({ alerts, loading, onChange }) {
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("open");

  const filtered = useMemo(() => {
    if (!alerts) return [];
    if (filter === "open") return alerts.filter(a => a.status !== "resolved" && a.status !== "false_positive");
    if (filter === "all") return alerts;
    return alerts.filter(a => a.severity === filter);
  }, [alerts, filter]);

  const ack = async (id, status) => {
    setBusy(id);
    try {
      await api.post(`/admin/threat/alerts/${id}/ack`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="alerts-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {["open", "critical", "high", "medium", "all"].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`alert-filter-${f}`}
            className={`rounded-full px-3 py-1.5 text-xs ring-1 transition-colors ${filter === f ? "bg-foreground text-background ring-foreground" : "bg-card ring-border hover:bg-secondary"}`}
          >
            {f === "open" ? "Open" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        {loading && <div className="px-4 py-10 text-center text-foreground/55"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2"/>Loading alerts…</div>}
        {!loading && filtered.length === 0 && (
          <div className="px-4 py-12 text-center text-foreground/55">
            <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2"/>
            No alerts in this filter. Quiet right now ✨
          </div>
        )}
        {!loading && filtered.map(a => {
          const meta = SEVERITY_META[a.severity] || SEVERITY_META.info;
          const status = a.status || "open";
          return (
            <div key={a.id} className="px-4 py-3 border-b border-border last:border-0" data-testid={`alert-${a.id}`}>
              <div className="flex items-start gap-3">
                <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${meta.dot}`}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{a.title}</span>
                    <Badge className={`text-[10px] ${meta.bg} ${meta.text}`}>{a.severity}</Badge>
                    <span className="text-[11px] text-foreground/55 mono">{a.rule}</span>
                    {status !== "open" && (
                      <Badge variant="outline" className="text-[10px]">{status.replace("_", " ")}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-foreground/75 mt-1">{a.body}</p>
                  <div className="text-[11px] text-foreground/55 mt-1 mono">
                    {timeAgo(a.created_at)}
                    {a.ack_by_email && ` · acked by ${a.ack_by_email}`}
                  </div>
                </div>
                {status !== "resolved" && status !== "false_positive" && (
                  <div className="flex gap-1 flex-shrink-0">
                    {status === "open" && (
                      <Button size="sm" variant="outline" disabled={busy === a.id} onClick={() => ack(a.id, "investigating")} className="text-xs rounded-full" data-testid={`alert-investigate-${a.id}`}>
                        Investigate
                      </Button>
                    )}
                    <Button size="sm" variant="outline" disabled={busy === a.id} onClick={() => ack(a.id, "resolved")} className="text-xs rounded-full" data-testid={`alert-resolve-${a.id}`}>
                      Resolve
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy === a.id} onClick={() => ack(a.id, "false_positive")} className="text-xs rounded-full text-foreground/55" data-testid={`alert-fp-${a.id}`}>
                      False positive
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── Notification preferences (Phase B) ────────────────────────────────
function NotificationPrefsTab() {
  const [prefs, setPrefs] = useState(null);
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/threat/notification-prefs/me");
      setPrefs(data.prefs);
      setRules(data.available_rules || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load preferences");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/threat/notification-prefs/me", patch);
      setPrefs(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!prefs) return <div className="py-10 text-center text-foreground/55"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2"/>Loading preferences…</div>;

  const ch = prefs.channels || {};
  const sev = prefs.severity_min || {};
  const qh = prefs.quiet_hours || {};

  return (
    <div className="space-y-6 max-w-3xl" data-testid="notification-prefs">
      {/* Channels */}
      <div>
        <h3 className="display text-lg mb-1">Channels</h3>
        <p className="text-sm text-foreground/60 mb-3">Where alerts reach you. In-app always recommended. Slack/email global webhooks must also be configured via env vars.</p>
        <div className="grid md:grid-cols-2 gap-2">
          {[
            { key: "in_app", label: "In-app notifications", desc: "Bell icon · always available · no setup" },
            { key: "email",  label: "Email",                desc: "Sent to your admin email via Resend" },
            { key: "slack",  label: "Slack",                desc: "Global #security channel via SECURITY_ALERTS_SLACK_WEBHOOK" },
            { key: "sms",    label: "SMS (critical only)",  desc: "Twilio · requires sms_number below" },
          ].map(c => (
            <label key={c.key} className="flex items-start gap-3 rounded-xl border border-border p-3 cursor-pointer hover:bg-secondary/30" data-testid={`channel-${c.key}`}>
              <input
                type="checkbox" checked={!!ch[c.key]}
                onChange={e => save({ channels: { ...ch, [c.key]: e.target.checked } })}
                disabled={busy}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-sm">{c.label}</div>
                <div className="text-[11px] text-foreground/55 mt-0.5">{c.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Severity thresholds */}
      <div>
        <h3 className="display text-lg mb-1">Severity thresholds</h3>
        <p className="text-sm text-foreground/60 mb-3">Minimum severity that fires each channel.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {["in_app", "email", "slack", "sms"].map(c => (
            <div key={c} className="rounded-xl border border-border p-3">
              <div className="text-sm font-medium mb-1">{c.replace("_", " ")}</div>
              <select
                value={sev[c] || "low"}
                onChange={e => save({ severity_min: { ...sev, [c]: e.target.value } })}
                disabled={busy}
                className="w-full text-sm rounded-lg border border-border bg-background px-2 py-1.5"
                data-testid={`sev-${c}`}
              >
                <option value="low">Low and up</option>
                <option value="medium">Medium and up</option>
                <option value="high">High and up</option>
                <option value="critical">Critical only</option>
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* SMS number */}
      <div>
        <h3 className="display text-lg mb-1">SMS number (optional)</h3>
        <p className="text-sm text-foreground/60 mb-3">Required only if SMS channel is on. Format: <span className="mono">+15551234567</span>.</p>
        <Input
          defaultValue={prefs.sms_number || ""}
          onBlur={e => e.target.value !== (prefs.sms_number || "") && save({ sms_number: e.target.value })}
          placeholder="+15551234567"
          disabled={busy}
          className="max-w-xs"
          data-testid="sms-number-input"
        />
      </div>

      {/* Quiet hours */}
      <div>
        <h3 className="display text-lg mb-1">Quiet hours (UTC)</h3>
        <p className="text-sm text-foreground/60 mb-3">Suppresses non-critical alerts during a daily window. Critical alerts always fire.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox" checked={!!qh.enabled}
              onChange={e => save({ quiet_hours: { ...qh, enabled: e.target.checked } })}
              disabled={busy}
              data-testid="quiet-hours-toggle"
            />
            <span className="text-sm">Enable</span>
          </label>
          {qh.enabled && (
            <>
              <div className="text-sm">From</div>
              <select
                value={qh.start_hour ?? 22}
                onChange={e => save({ quiet_hours: { ...qh, start_hour: Number(e.target.value) } })}
                disabled={busy}
                className="text-sm rounded-lg border border-border bg-background px-2 py-1.5"
                data-testid="quiet-start"
              >
                {[...Array(24).keys()].map(h => <option key={h} value={h}>{String(h).padStart(2,"0")}:00</option>)}
              </select>
              <div className="text-sm">to</div>
              <select
                value={qh.end_hour ?? 7}
                onChange={e => save({ quiet_hours: { ...qh, end_hour: Number(e.target.value) } })}
                disabled={busy}
                className="text-sm rounded-lg border border-border bg-background px-2 py-1.5"
                data-testid="quiet-end"
              >
                {[...Array(24).keys()].map(h => <option key={h} value={h}>{String(h).padStart(2,"0")}:00</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      {/* Muted rules */}
      <div>
        <h3 className="display text-lg mb-1">Muted rules</h3>
        <p className="text-sm text-foreground/60 mb-3">Specific alert rules to ignore. Use sparingly — muting critical rules is risky.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {rules.map(rule => {
            const muted = (prefs.muted_rules || []).includes(rule);
            return (
              <label key={rule} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`mute-${rule}`}>
                <input
                  type="checkbox" checked={muted}
                  onChange={e => {
                    const next = e.target.checked
                      ? [...(prefs.muted_rules || []), rule]
                      : (prefs.muted_rules || []).filter(r => r !== rule);
                    save({ muted_rules: next });
                  }}
                  disabled={busy}
                />
                <span className={muted ? "text-foreground/50 line-through" : ""}>{rule}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ─── Top-level page ────────────────────────────────────────────────────
export default function AdminThreatCenter() {
  const [tab, setTab] = useState("live");
  const [overview, setOverview] = useState(null);
  const [topIps, setTopIps] = useState([]);
  const [feed, setFeed] = useState([]);
  const [blocks, setBlocks] = useState(null);
  const [rules, setRules] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const refreshAlerts = async () => {
    setAlertsLoading(true);
    try {
      const { data } = await api.get("/admin/threat/alerts?limit=200");
      setAlerts(data.alerts || []);
    } catch (e) {
      // silent — alerts is secondary panel
    } finally { setAlertsLoading(false); }
  };

  const refresh = async () => {
    try {
      const [ov, tip, fd, bl, rl] = await Promise.all([
        api.get("/admin/threat/overview"),
        api.get("/admin/threat/top-ips?window_minutes=60&limit=20"),
        api.get("/admin/threat/feed?limit=200"),
        api.get("/admin/security/blocked-ips"),
        api.get("/admin/security/auto-ban-rules"),
      ]);
      setOverview(ov.data);
      setTopIps(tip.data.top_ips || []);
      setFeed(fd.data.events || []);
      setBlocks(bl.data);
      setRules(rl.data);
      refreshAlerts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load threat data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Poll every 15s when Live tab is visible
    pollRef.current = setInterval(() => {
      if (tab === "live") refresh();
    }, 15000);
    return () => clearInterval(pollRef.current);
  }, [tab]);

  const quickBlock = async (ip) => {
    try {
      await api.post("/admin/security/blocked-ips", {
        ip, reason: `quick-block from Threat Live (top-IP heatmap)`, duration_hours: 24,
      });
      toast.success(`Blocked ${ip} for 24h`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Block failed");
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-threat-center">
      <div>
        <h1 className="display text-3xl tracking-tight">Threat Center</h1>
        <p className="text-sm text-foreground/65 mt-1 max-w-2xl">
          Real-time security operations: live attack detection, IP block list, kill-switch, and
          force-logout. Every action stamps an immutable row in <span className="mono">security_events</span>.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border flex gap-1 overflow-x-auto">
        {[
          { id: "live",        label: "Live",          icon: Activity },
          { id: "alerts",      label: "Alerts",        icon: BellRing, badge: alerts.filter(a => a.status !== "resolved" && a.status !== "false_positive").length },
          { id: "controls",    label: "Controls",      icon: Shield },
          { id: "kill_switch", label: "Kill switch",   icon: Power },
          { id: "prefs",       label: "Notifications", icon: Bell },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`threat-tab-${t.id}`}
              className={`px-4 py-2 text-sm border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap ${tab === t.id ? "border-foreground text-foreground" : "border-transparent text-foreground/55 hover:text-foreground/80"}`}
            >
              <Icon className="w-4 h-4"/>{t.label}
              {t.badge > 0 && <Badge className="bg-red-50 text-red-700 ring-1 ring-red-200 text-[10px] ml-1">{t.badge}</Badge>}
            </button>
          );
        })}
      </div>

      {tab === "live"        && <LiveTab     overview={overview} topIps={topIps} feed={feed} loading={loading} onRefresh={refresh} onBlockIp={quickBlock} />}
      {tab === "alerts"      && <AlertsTab   alerts={alerts} loading={alertsLoading} onChange={refreshAlerts} />}
      {tab === "controls"    && <ControlsTab blocks={blocks} rules={rules}    onRefresh={refresh} onChange={refresh} />}
      {tab === "kill_switch" && <KillSwitchTab state={overview?.kill_switch}  onChange={refresh} />}
      {tab === "prefs"       && <NotificationPrefsTab />}
    </div>
  );
}
